import { SignInPage } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  createExtension,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  IconBundleBlueprint,
  SignInPageBlueprint,
  SignInPageProps,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { SvgIconProps, Typography } from '@material-ui/core';
import { GitflicIcon } from '../../components/icons/GitflicIcon';
import { oidcAuthApiRef } from 'plugin-app-module-adp-oidc-session-logout';
import { themes } from '../../theme/theme';
import { appTranslationRef } from '../../translation';
import { translateServerErrorMessage } from '../../translations/serverErrorMessages.ru';

const AdpSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const { t } = useTranslationRef(appTranslationRef);
  const useGuestAuthForE2E =
    configApi.has('auth.providers.guest') ||
    new URLSearchParams(window.location.search).get('e2e-auth') === 'guest';

  if (useGuestAuthForE2E) {
    return <SignInPage {...props} providers={['guest']} />;
  }

  return (
    <SignInPage
      {...props}
      auto
      provider={{
        id: 'oidc',
        title: t('signIn.provider.title'),
        message: t('signIn.provider.message'),
        apiRef: oidcAuthApiRef,
      }}
      ErrorComponent={({ error }) => (
        <Typography variant="body1" color="error">
          {error?.name === 'PopupClosedError' &&
          error.message === 'Login failed, popup was closed'
            ? t('signIn.errors.popupClosed')
            : translateServerErrorMessage(error?.message ?? '')}
        </Typography>
      )}
    />
  );
};

const disableThemeExtension = (id: string) =>
  createExtension({
    kind: 'theme',
    name: id,
    attachTo: { id: 'api:app/app-theme', input: 'themes' },
    disabled: true,
    output: [],
    factory: () => [],
  });

const GitflicIconInherit = (props: SvgIconProps) => (
  <GitflicIcon {...props} style={{ ...props.style, fontSize: 'inherit' }} />
);

export const appOptionsModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    IconBundleBlueprint.make({
      name: 'app-options',
      params: {
        icons: {
          gitflic: GitflicIconInherit,
        },
      },
    }),
    ...themes.map(theme =>
      ThemeBlueprint.make({
        name: theme.id,
        params: { theme },
      }),
    ),
    ...['light', 'dark']
      .filter(id => !themes.some(theme => theme.id === id))
      .map(disableThemeExtension),
    SignInPageBlueprint.make({
      params: {
        loader: async () => AdpSignInPage,
      },
    }),
  ],
});
